<template>
    <div class="investment-platform">
        <v-container fluid>
            <!-- Header -->
            <v-row>
                <v-col cols="12">
                    <v-card class="platform-header mb-6" elevation="4">
                        <v-card-title class="text-h4 primary--text text-center py-8">
                            <div class="w-100">
                                <v-icon x-large left color="primary">mdi-trending-up</v-icon>
                                <div class="text-h3 font-weight-bold">Investičná platforma</div>
                                <div class="text-h6 grey--text mt-2">Staňte sa súčasťou nášho úspechu</div>
                            </div>
                        </v-card-title>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Tabuľka 1: Modelácia výšky investície (VZOR pre investorov) -->
            <v-row class="mb-6">
                <v-col cols="12">
                    <investment-calculator ref="calculator" :defaultAnnualRevenue="defaultAnnualRevenue"
                        :totalInvestments="totalInvestments" :maxInvestmentAmount="maxInvestmentAmount"
                        @invest="handleInvestmentSubmission" @annual-revenue-change="handleAnnualRevenueChange"
                        @show-contract-preview="showContractPreview" />
                </v-col>
            </v-row>

            <!-- Tabuľka 2: Reálna osobná tabuľka investora -->
            <v-row v-if="isAuthenticated" class="mb-6">
                <v-col cols="12">
                    <personal-investments-table :investments="userInvestments" :annualRevenue="modeledAnnualRevenue"
                        :loading="loadingUserInvestments" @start-investing="scrollToCalculator"
                        @refresh="loadUserInvestments" @show-contract="showContractDetail"
                        @download-contract="downloadContract" @invest-additional="handleAdditionalInvestment" />
                </v-col>
            </v-row>

            <!-- Tabuľka 3: Koľko treba vrátiť investorom -->
            <v-row class="mb-6">
                <v-col cols="12">
                    <investment-overview-table :totalInvestments="totalInvestments"
                        :totalInvestmentLevels="investmentLevels" :totalPayoutRequired="totalPayoutRequired"
                        :actualPayouts="actualPayouts" :maxInvestmentAmount="maxInvestmentAmount" />
                </v-col>
            </v-row>

            <!-- Tabuľka 4: Predpokladané ročné tržby -->
            <v-row class="mb-6">
                <v-col cols="12">
                    <revenue-forecast-table :projects="projects" :annualRevenue="modeledAnnualRevenue"
                        @revenue-change="handleAnnualRevenueChange" />
                </v-col>
            </v-row>

            <!-- Informačná sekcia -->
            <v-row class="mb-6">
                <v-col cols="12">
                    <v-card elevation="2">
                        <v-card-title class="warning--text">
                            <v-icon left color="warning">mdi-information</v-icon>
                            Dôležité informácie
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-alert type="info" outlined dense>
                                        <div class="font-weight-bold mb-1">Minimálna výška investície</div>
                                        <div class="text-h6 primary--text">10 €</div>
                                        <div class="text-caption">Investovať môžete len násobky 10 €</div>
                                    </v-alert>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-alert type="warning" outlined dense>
                                        <div class="font-weight-bold mb-1">Platnosť zmluvy</div>
                                        <div class="text-body-1 mb-3">
                                            Prečítajte si často kladené otázky a návody.
                                        </div>
                                        <v-btn color="info" outlined large>
                                            <v-icon left>mdi-book-open</v-icon>
                                            Dokumentácia
                                        </v-btn>
                                    </v-alert>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <!-- Login redirect dialog -->
        <v-dialog v-model="loginDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon left color="white">mdi-account</v-icon>
                    Prihlásenie potrebné
                </v-card-title>
                <v-card-text class="pa-6">
                    <div class="text-center">
                        <v-icon size="80" color="primary" class="mb-4">mdi-account-circle</v-icon>
                        <div class="text-h6 mb-3">Pre investovanie sa musíte prihlásiť</div>
                        <div class="text-body-1 grey--text mb-4">
                            Vytvorte si účet alebo sa prihláste do existujúceho účtu.
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-btn color="grey" text @click="loginDialog = false">
                        Zrušiť
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="success" @click="redirectToRegister">
                        Registrácia
                    </v-btn>
                    <v-btn color="primary" @click="redirectToLogin">
                        Prihlásenie
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Contract Preview Dialog -->
        <contract-preview-dialog v-model="contractDialog" :investmentAmount="plannedInvestmentAmount"
            :userInfo="userInfo" :investmentBreakdown="contractBreakdown" @confirm="confirmInvestment" />

        <!-- Success Dialog -->
        <v-dialog v-model="successDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="success white--text">
                    <v-icon left color="white">mdi-check-circle</v-icon>
                    Investícia vytvorená
                </v-card-title>
                <v-card-text class="pa-6">
                    <div class="text-center">
                        <v-icon size="80" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
                        <div class="text-h6 mb-2">Úspešne ste vytvorili investíciu!</div>
                        <div class="text-body-1 mb-4">
                            Investícia vo výške <strong>{{ formatCurrency(plannedInvestmentAmount) }}</strong>
                            bola úspešne vytvorená.
                        </div>

                        <v-alert type="warning" outlined class="mb-4">
                            <strong>Nezabudnite uhradiť investíciu!</strong><br>
                            Použite platobné údaje z náhľadu zmluvy. Investícia sa stane aktívnou až po pripísaní sumy
                            na náš účet.
                        </v-alert>

                        <div class="mb-4">
                            <div class="text-caption mb-1">Vaše investičné ID (variabilný symbol):</div>
                            <v-chip color="primary" large>{{ userInfo.id }}</v-chip>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" large @click="successDialog = false">
                        Pokračovať
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Loading overlay -->
        <v-overlay :value="loading">
            <div class="text-center">
                <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
                <div class="text-h6 mt-4">Spracováva sa...</div>
            </div>
        </v-overlay>
    </div>
</template>

<script>
// Import komponentov
import InvestmentCalculator from '@/components/invest/InvestmentCalculator.vue';
import PersonalInvestmentsTable from '@/components/invest/PersonalInvestmentsTable.vue';
import InvestmentOverviewTable from '@/components/invest/InvestmentOverviewTable.vue';
import RevenueForecastTable from '@/components/invest/RevenueForecastTable.vue';
import ContractPreviewDialog from '@/components/invest/ContractPreviewDialog.vue';

// Mock API service
const APIService = {
    getInvestmentStats() {
        return Promise.resolve({
            totalInvestments: 2687420,
            actualPayouts: 370970.6,
            totalContracts: 150,
            uniqueInvestors: 89
        });
    },

    getRevenueForecast() {
        return Promise.resolve({
            defaultAnnualRevenue: 3420000
        });
    },

    getUserInvestments() {
        return Promise.resolve({
            results: [
                {
                    id: 1,
                    contract_number: '123/1',
                    investment_amount: 1000,
                    coefficient: 5,
                    payout_claim: 5000,
                    status: 'active',
                    created_at: '2024-01-15',
                    payment_date: '2024-01-16',
                    is_split: false
                },
                {
                    id: 2,
                    contract_number: '123/2',
                    investment_amount: 100000,
                    coefficient: 5,
                    payout_claim: 500000,
                    status: 'active',
                    created_at: '2024-02-01',
                    payment_date: '2024-02-02',
                    is_split: false
                }
            ]
        });
    },

    getMe() {
        return Promise.resolve({
            id: 123,
            first_name: 'Ján',
            last_name: 'Novák',
            email: 'jan.novak@example.com',
            address: 'Bratislava, Slovensko'
        });
    },

    createInvestment(data) {
        return Promise.resolve({ success: true, id: Math.random() });
    }
};

export default {
    name: "InvestmentPlatform",

    components: {
        InvestmentCalculator,
        PersonalInvestmentsTable,
        InvestmentOverviewTable,
        RevenueForecastTable,
        ContractPreviewDialog
    },

    data() {
        return {
            loading: false,
            loadingUserInvestments: false,
            loginDialog: false,
            contractDialog: false,
            successDialog: false,
            plannedInvestmentAmount: 0,
            contractBreakdown: [],

            // Default values based on Excel analysis
            defaultAnnualRevenue: 3420000, // Z Excel analýzy - prvý rok
            modeledAnnualRevenue: 3420000,
            totalInvestments: 2687420, // Z Excel analýzy
            maxInvestmentAmount: 10000000,

            // Investment levels with coefficients (z Excel komentárov)
            investmentLevels: [
                { threshold: 1000000, investment_amount: 1000000, coefficient: 5, total_payout: 5000000 },
                { threshold: 2000000, investment_amount: 1000000, coefficient: 4, total_payout: 4000000 },
                { threshold: 3000000, investment_amount: 1000000, coefficient: 3, total_payout: 3000000 },
                { threshold: 4000000, investment_amount: 1000000, coefficient: 2, total_payout: 2000000 },
                { threshold: 10000000, investment_amount: 6000000, coefficient: 1.5, total_payout: 9000000 }
            ],

            totalPayoutRequired: 23000000, // Z Excel analýzy
            actualPayouts: 370970.6, // Z Excel tabuľky reálne tržby

            // Projects for revenue forecasting (z Excel)
            projects: [
                { id: 1, name: "FICHEE TAXI", category: "transport", year1: 1200000, year2: 3600000, year3: 6000000 },
                { id: 2, name: "FICHEE RENTAL + SHARING", category: "transport", year1: 1200000, year2: 3600000, year3: 6000000 },
                { id: 3, name: "FICHEE KURIÉR", category: "transport", year1: 1200000, year2: 3600000, year3: 6000000 },
                { id: 4, name: "FICHEE GUARD", category: "security", year1: 1200000, year2: 3600000, year3: 6000000 },
                { id: 5, name: "Predaj z dvora", category: "retail", year1: 1200000, year2: 3600000, year3: 6000000 },
                { id: 6, name: "Holidaydom", category: "advertising", year1: 1200000, year2: 3600000, year3: 6000000 }
            ],

            userInvestments: [],
            userInfo: {
                id: 123,
                first_name: 'Demo',
                last_name: 'Investor',
                email: 'demo@example.com',
                address: 'Demo adresa'
            }
        };
    },

    computed: {
        // Mock authentication state
        isAuthenticated() {
            // V reálnej aplikácii by toto bolo z Vuex store alebo inej state management
            return true; // Pre demo účely
        },

        username() {
            return this.isAuthenticated ? 'demo_user' : null;
        },

        firstName() {
            return this.userInfo.first_name;
        },

        lastName() {
            return this.userInfo.last_name;
        }
    },

    mounted() {
        this.loadInitialData();
        if (this.isAuthenticated) {
            this.loadUserInvestments();
            this.loadUserInfo();
        }
    },

    methods: {
        loadInitialData() {
            this.loading = true;

            // Load investment platform data
            Promise.all([
                APIService.getInvestmentStats(),
                APIService.getRevenueForecast()
            ]).then(([statsResponse, revenueResponse]) => {
                if (statsResponse) {
                    this.totalInvestments = statsResponse.totalInvestments || this.totalInvestments;
                    this.actualPayouts = statsResponse.actualPayouts || 0;
                }

                if (revenueResponse) {
                    this.defaultAnnualRevenue = revenueResponse.defaultAnnualRevenue || this.defaultAnnualRevenue;
                    this.modeledAnnualRevenue = this.defaultAnnualRevenue;
                }

                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },

        loadUserInvestments() {
            this.loadingUserInvestments = true;

            APIService.getUserInvestments().then((response) => {
                this.userInvestments = response.results || [];
                this.loadingUserInvestments = false;
            }).catch(() => {
                this.loadingUserInvestments = false;
            });
        },

        loadUserInfo() {
            APIService.getMe().then((response) => {
                this.userInfo = response;
            });
        },

        handleInvestmentSubmission(amount) {
            if (!this.isAuthenticated) {
                this.loginDialog = true;
                return;
            }

            this.plannedInvestmentAmount = amount;
            // Pre demo - jednoduché zobrazenie dialógu
            this.showInvestmentSuccess();
        },

        handleAdditionalInvestment(amount) {
            this.handleInvestmentSubmission(amount);
        },

        showContractPreview(data) {
            if (!this.isAuthenticated) {
                this.loginDialog = true;
                return;
            }

            this.plannedInvestmentAmount = data.amount;
            this.contractBreakdown = data.breakdown || [];
            this.contractDialog = true;
        },

        showInvestmentSuccess() {
            // Pre demo - priamo zobrazíme success dialog
            this.successDialog = true;
        },

        confirmInvestment(investmentData) {
            this.loading = true;

            APIService.createInvestment(investmentData).then(() => {
                this.contractDialog = false;
                this.successDialog = true;
                this.loadUserInvestments();
                this.loadInitialData();
            }).catch((error) => {
                console.error('Chyba pri vytváraní investície:', error);
                // V reálnej aplikácii by tu bol toast alebo iná notifikácia
            }).finally(() => {
                this.loading = false;
            });
        },

        handleAnnualRevenueChange(newRevenue) {
            this.modeledAnnualRevenue = newRevenue;
        },

        scrollToCalculator() {
            if (this.$refs.calculator) {
                this.$refs.calculator.$el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        },

        showContractDetail(investment) {
            // Zobrazenie detailu zmluvy
            console.log('Show contract detail:', investment);
        },

        downloadContract(investment) {
            // Stiahnutie zmluvy
            console.log('Download contract:', investment);
        },

        redirectToLogin() {
            this.loginDialog = false;
            // V reálnej aplikácii: this.$router.push('/login');
            console.log('Redirect to login');
        },

        redirectToRegister() {
            this.loginDialog = false;
            // V reálnej aplikácii: this.$router.push('/register');
            console.log('Redirect to register');
        },

        formatCurrency(amount) {
            if (typeof amount !== 'number') return '0€';
            return new Intl.NumberFormat('sk-SK', {
                style: 'currency',
                currency: 'EUR'
            }).format(amount);
        }
    }
};
</script>

<style scoped>
.investment-platform {
    background-color: #f8f9fa;
    min-height: 100vh;
}

.platform-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.platform-header .v-card__title {
    color: white !important;
}

.platform-header .primary--text {
    color: white !important;
}

.v-container {
    max-width: 1400px;
}

@media (max-width: 600px) {
    .text-h3 {
        font-size: 1.8rem !important;
    }

    .text-h6 {
        font-size: 1.1rem !important;
    }

    .v-container {
        padding: 8px;
    }
}
</style>